import { DialogContent, Box, Grid, TextField, DialogActions, Dialog, DialogTitle, IconButton, styled, Paper, Button, Autocomplete } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import { IChemical } from "../../interfaces/Growing/IChemical";
import axios from "axios";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { ISprayRecord } from "../../interfaces/Growing/ISprayRecord";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IGreenhouse } from "../../interfaces/IGreenhouse";
import { IBay } from "../../interfaces/IBay";
import { IUser } from "../../interfaces/IUser";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#666666' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2, }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

type Props = {
    isOpen: boolean,
    handleClose: () => void,
    sprayRecordId?: number | undefined,
};

export default function SprayRecordDialog(props: Props) {
    const { isOpen, handleClose } = props;
    const { register, handleSubmit, control, formState: { errors }, getValues, reset, setValue } = useForm<ISprayRecord>();
    const [chemicals, setChemicals] = useState<IChemical[]>([]);
    const [selectedChemical, setSelectedChemical] = useState<IChemical>();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [greenhouses, setGreenhouses] = useState<IGreenhouse[]>([]);
    const [selectedGreenhouse, setSelectedGreenhouse] = useState<IGreenhouse>();
    const [bays, setBays] = useState<IBay[]>([]);
    const [selectedBay, setSelectedBay] = useState<IBay>();
    const [species, setSpecies] = useState<string[]>([]);
    const [selectedSpecies, setSelectedSpecies] = useState<string>();
    const [growers, setGrowers] = useState<IUser[]>([]);
    const [handlers, setHandlers] = useState<IUser[]>([]);
    const [selectedGrower, setSelectedGrower] = useState<IUser>();
    const [selectedHandler, setSelectedHandler] = useState<IUser>();

    function ResetForm(id: number | undefined) {
        reset({});
        axios.get<IChemical[]>(`${process.env.REACT_APP_ENDPOINT}/api/Chemicals`).then((response) => {
            setChemicals(response.data);
        });
        axios.get<IGreenhouse[]>(`${process.env.REACT_APP_ENDPOINT}/api/Greenhouses`).then((response) => {
            setGreenhouses([]);
            setGreenhouses(response.data);
        });
        axios.get<IUser[]>(`${process.env.REACT_APP_ENDPOINT}/api/Users`).then((response) => {
            setGrowers(response.data);
        });
        axios.get<IUser[]>(`${process.env.REACT_APP_ENDPOINT}/api/Users`).then((response) => {
            setHandlers(response.data);
        });

        if (id) {
            axios.get<ISprayRecord>(`${process.env.REACT_APP_ENDPOINT}/api/SprayRecords/${id}`).then((response) => {
                let record = response.data;
                record.greenhouse = greenhouses.find(ele => ele.id == response.data.greenhouseId);
                reset(response.data);
                setSelectedGreenhouse(greenhouses.find(ele => ele.id == response.data.greenhouseId))
            });
        }

        setSelectedDate(moment());
    }

    useEffect(() => {
        ResetForm(props.sprayRecordId);
    }, [props])

    useEffect(() => {
        if (selectedGreenhouse) {
            setBays(selectedGreenhouse?.bays);
        }
    }, [selectedGreenhouse])

    useEffect(() => {
        if (selectedBay) {
            axios.get(`${process.env.REACT_APP_ENDPOINT}/api/Bays/Species/${selectedBay.id}`).then((response) => {
                setSpecies(response.data);
            })
        }
    }, [selectedBay])

    useEffect(() => {
        setValue("appliedAt", selectedDate)
        let endMoment = moment(selectedDate).add(getValues("chemical.rei"), 'hours')
        setValue("reentryAt", endMoment);
    }, [selectedDate])

    useEffect(() => {
        console.log("IN CHANGE USE EFFECT");
        if (selectedChemical != null) {
            let appliedTime = getValues('appliedAt');
            let endTime = moment(appliedTime).add(selectedChemical.rei, 'hours');
            setValue('reentryAt', endTime);
            let record : ISprayRecord = {
                chemical: selectedChemical
            }
            reset(record);
        } else {
            reset({});
        }

    }, [selectedChemical])

    const onSubmit: SubmitHandler<ISprayRecord> = data => {
        data.greenhouseId = selectedGreenhouse?.id;
        if (data.id == undefined) {
            data.id = 0
        }
        if (data.id && data.id > 0) {
            axios.put(`${process.env.REACT_APP_ENDPOINT}/api/SprayRecords/${data.id}`, data);
            // handleClose();
        } else {
            console.log("SPRAY RECORD", data);
            axios.post(`${process.env.REACT_APP_ENDPOINT}/api/SprayRecords`, data);
        }
        // handleClose();
    };

    return (
        <div>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={isOpen}
                maxWidth='xl'
                fullWidth
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
                    Add Spray Record
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Box sx={{ flexGrow: 1 }} component="form" onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            sx={{ display: "none" }}
                            required
                            variant="outlined"
                            fullWidth
                            id="id"
                            label="ID"
                            InputLabelProps={{ shrink: true }}
                            {...register('id')}
                            disabled />
                        <Grid container spacing={3} sx={{ padding: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name="chemical"
                                        render={({ field: { onChange, value } }) => {
                                            let tempValue = chemicals.findIndex(x => x.id == value.id);
                                            return (
                                                <Autocomplete
                                                    value={value}
                                                    isOptionEqualToValue={(option, value) => option.id === value?.id }
                                                    options={chemicals}
                                                    getOptionLabel={(option) => {return option.id != undefined ? `${option.id} - ${option.name} - ${option.activeIngrediant}` : ""} }
                                                    onChange={(_, data) => {
                                                        setSelectedChemical(data!);
                                                        return onChange(data);
                                                    } }
                                                    renderInput={(params) => (
                                                        <TextField {...params} fullWidth variant="outlined" label="Chemical Item" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                    )} />
                                            );
                                        }}
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="epaNumber"
                                        label="EPA Reg No"
                                        InputLabelProps={{ shrink: true }}
                                        {...register("chemical.epaNumber")}
                                        disabled />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="activeingredient"
                                        label="Active Ingredient"
                                        disabled
                                        InputLabelProps={{ shrink: true }}
                                        {...register("chemical.activeIngrediant")} />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="rei"
                                        label="REI"
                                        
                                        InputLabelProps={{ shrink: true }}
                                        {...register("chemical.rei", {
                                            valueAsNumber: true,
                                            onChange: (evt) => {
                                                let appliedTime = getValues('appliedAt');
                                                console.log("Applied Time 1", appliedTime);
                                                let endTime = moment(appliedTime).add(evt.target.value, 'hours');
                                                console.log("End Time", endTime);
                                                setValue('reentryAt', endTime);
                                                console.log("GET VALUES", getValues('reentryAt'));
                                            }
                                        })} />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        {...register("appliedAt")}
                                        control={control}
                                        render={
                                            ({ field: { onChange, ...restField } }) =>
                                                <DateTimePicker
                                                    label="Applied At"
                                                    onChange={(event) => { onChange(event); setSelectedDate(moment(event!)) }}
                                                    renderInput={(params) =>
                                                        <TextField
                                                            fullWidth
                                                            variant="outlined"
                                                            {...params}
                                                        />}
                                                    {...restField}
                                                />
                                        }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        {...register('reentryAt')}
                                        control={control}
                                        render={
                                            ({ field: { onChange, ...restField } }) =>
                                                <DateTimePicker
                                                    label="Reentry Available At"
                                                    disabled
                                                    onChange={(event) => { onChange(event); }}
                                                    renderInput={(params) =>
                                                        <TextField
                                                            fullWidth
                                                            disabled
                                                            variant="outlined"
                                                            {...params}
                                                        />}
                                                    {...restField}
                                                />
                                        }
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name="greenhouse"
                                        render={({ field: { ref, onChange, value, ...field } }) => {
                                            console.log(value);
                                            let tempValue = greenhouses.findIndex(x => x.id == value?.id);
                                            return (
                                                <Autocomplete
                                                    value={value}
                                                    isOptionEqualToValue={(option, value) => option.id === value?.id }
                                                    options={greenhouses}
                                                    getOptionLabel={(option) => `${option.id} - ${option.name} - ${option.availableSpace}`}
                                                    onChange={(_, data) => {
                                                        setSelectedGreenhouse(data!);
                                                        console.log("GREENHOUSE DATA", data);
                                                        return onChange(data);
                                                    } }
                                                    renderInput={(params) => (
                                                        <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Greenhouse" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                    )} />
                                            );
                                        }}
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name="bay"
                                        render={({ field: { ref, onChange, ...field } }) => (
                                            <Autocomplete
                                                options={bays}
                                                getOptionLabel={(option) => `${option.id} - ${option.name} - ${option.availableSpace}`}
                                                onChange={(_, data) => {
                                                    setSelectedBay(data!);
                                                    return onChange(data);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Bay" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                )}
                                            />
                                        )}
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name='species'
                                        render={({ field: { ref, onChange, ...field } }) => (
                                            <Autocomplete
                                                options={species}
                                                getOptionLabel={(option) => `${option}`}
                                                onChange={(_, data) => {
                                                    setSelectedSpecies(data!);
                                                    return onChange(data);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Species" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                )}
                                            />
                                        )}
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="rate"
                                        label="Rate"
                                        InputLabelProps={{ shrink: true }}
                                        {...register('rate')} />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="crops"
                                        label="Gallons Applied"
                                        InputLabelProps={{ shrink: true }}
                                        {...register('gallonsApplied')} />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        id="targetpest"
                                        label="Target Pest"
                                        InputLabelProps={{ shrink: true }}
                                        disabled
                                        {...register('chemical.targetPest')} />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name='handler'
                                        render={({ field: { ref, onChange, value, ...field } }) => (
                                            <Autocomplete
                                                value={value}
                                                options={handlers}
                                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                                onChange={(_, data) => {
                                                    setSelectedHandler(data!);
                                                    return onChange(data);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Handlers" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                )}
                                            />
                                        )}
                                    />
                                </Item>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Item sx={{ padding: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
                                    <Controller
                                        control={control}
                                        name='grower'
                                        render={({ field: { ref, onChange, value, ...field } }) => (
                                            <Autocomplete
                                                value={value}
                                                options={growers}
                                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                                onChange={(_, data) => {
                                                    setSelectedGrower(data!);
                                                    return onChange(data);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Growers" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                )}
                                            />
                                        )}
                                    />
                                </Item>
                            </Grid>

                        </Grid>
                        <DialogActions>
                            <Button size="large" variant="outlined" onClick={handleClose}>Cancel</Button>
                            {(getValues("id") != undefined && getValues("id")! > 0) && <Button size="large" color="success" variant="contained" onClick={handleSubmit(onSubmit)} autoFocus>
                                UPDATE
                            </Button>
                            }

                            {(getValues("id") == undefined || getValues("id")! == 0) && <Button size="large" color="success" variant="contained" onClick={handleSubmit(onSubmit)} autoFocus>
                                CREATE
                            </Button>
                            }
                        </DialogActions>
                    </Box>
                </DialogContent>
            </BootstrapDialog>
        </div>
    )
}
